import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap, useScrollLock } from '@/shared/hooks/useFocusTrap';
import { X } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './Modal.module.scss';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** `form` limita a 560px; `content` a 720px; `wide` a 960px — formulário com duas colunas. */
  size?: 'form' | 'content' | 'wide';
  children: ReactNode;
  /** Acoes do rodape. A primaria fica a direita. */
  footer?: ReactNode;
  className?: string;
}

/**
 * Modal centralizado.
 *
 * Foco preso dentro, `Esc` fecha, foco volta ao gatilho e o `<body>` para de
 * rolar enquanto estiver aberto — os quatro comportamentos que separam um modal
 * acessivel de uma `<div>` sobreposta.
 *
 * Abaixo de `md` o padrao do sistema e o `BottomSheet`, nao este componente.
 */
export function Modal({ open, onClose, title, size = 'form', children, footer, className }: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useFocusTrap(dialogRef, open, onClose);
  useScrollLock(open);

  if (!open) return null;

  return createPortal(
    <div className={styles.backdrop} onMouseDown={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(styles.dialog, styles[size], className)}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 className={styles.title} id={titleId}>
            {title}
          </h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Fechar">
            <Icon as={X} size={20} />
          </button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
